import { SafeLink } from "@/components/ui/SafeLink";
import { Button } from "@/components/ui/button";
import { SlideFromLeft, SlideFromRight } from "@/components/ui/animations";

export default function Tiles() {
    return (
        <div className="flex flex-col w-full bg-gradient-to-b from-gray-50 to-gray-100  relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-32 h-32 bg-red-500/3 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/3 right-1/4 w-48 h-48 bg-gray-900/2 rounded-full blur-3xl"></div>
            </div>

            {/* Appliances */}
            <SlideFromLeft Component={
                <div id="appliances" className="  md:pl-0 lg:px-6 xl:px-14 relative z-10 py-14">
                    <div className=" flex flex-col lg:flex-row items-center gap-8 md:gap-10 lg:gap-12 justify-center ">
                        <div className="relative group flex-shrink-0 px-4 md:px-6 lg:px-0 transform transition-all duration-700 hover:-translate-y-3 hover:scale-105">
                            <img
                                loading="eager"
                                className="md:w-full lg:max-w-xl xl:max-w-2xl w-full h-full lg:h-72 xl:h-80 object-cover rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-700"
                                src={`${process.env.REACT_APP_BLOB_URL}/www/img/sector/health-aged-care-residential-household-appliances.jpg`}
                                alt="Senior preparing vegetables in kitchen featuring in-built oven, electric hob, and coffee machine"
                            />
                            {/* Dark overlay for hover effect */}
                            <div className="absolute inset-0 bg-black/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                            {/* Animated shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-2xl"></div>
                        </div>

                        <div className="flex flex-col gap-10 md:gap-8 lg:px-0 px-4 text-center lg:text-left ">
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 max-w-lg sm:mx-auto md:mx-auto lg:mx-0 lg:max-w-xl leading-tight">
                                <span className="hero-text-gradient-red">Appliances </span>Designed <br />for Senior Comfort
                            </h2>

                            <p className="text-lg  text-gray-600 leading-relaxed max-w-2xl">
                                We provide energy-efficient, easy-to-use appliances selected for safety, accessibility, and quiet operation. Our range includes both residential and commercial options—all tested for reliability and long-term performance.
                            </p>

                        </div>
                    </div>
                </div>
            } />

            {/* Subtle section separator */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300/50 to-transparent mx-auto"></div>

            {/* Furniture & Bedding */}
            <SlideFromRight Component={
                <div id="furniture" className="md:pl-0 lg:px-6 xl:px-12 py-14 relative z-10">
                    <div className="flex flex-col lg:flex-row-reverse items-center gap-8 md:gap-10 lg:gap-12 justify-center ">
                        <div className="relative group flex-shrink-0 px-4 md:px-6 lg:px-0 transform transition-all duration-700 hover:-translate-y-3 hover:scale-105">
                            <img
                                loading="eager"
                                className="md:w-full lg:max-w-xl xl:max-w-2xl w-full h-full lg:h-72 xl:h-80 object-cover rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-700"
                                src={`${process.env.REACT_APP_BLOB_URL}/www/img/products/hnc-furniture-accommodation-rental2.jpg`}
                                alt="Comfortable aged care living space with ergonomic sofa and accessible kitchen—ideal for retirement homes."
                            />
                            {/* Dark overlay for hover effect */}
                            <div className="absolute inset-0 bg-black/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                            {/* Animated shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-2xl"></div>
                        </div>

                        <div className="flex flex-col gap-10 md:gap-4 lg:px-0 px-4 text-center lg:text-left">
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 max-w-lg sm:mx-auto md:mx-auto lg:mx-0 lg:max-w-xl leading-tight">
                                Wellbeing-Driven <br /><span className="hero-text-gradient-red">Furniture & Bedding </span>
                            </h2>
                            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
                                Every piece is carefully chosen to promote comfort, mobility support, and aesthetic appeal. From ergonomic seating to specialist bedding, we ensure your residents feel at home—beautifully and safely.
                            </p>
                        </div>
                    </div>
                </div>
            } />

            {/* Subtle section separator */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300/50 to-transparent mx-auto"></div>

            {/* Entertainment */}
            <SlideFromLeft Component={
                <div id="turn-key" className="md:pl-0 lg:px-6 xl:px-12 py-14 relative z-10">
                    <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-10 lg:gap-12 justify-center">
                        <div className="relative group flex-shrink-0 px-4 md:px-6 lg:px-0 transform transition-all duration-700 hover:-translate-y-3 hover:scale-105">
                            <img
                                loading="eager"
                                className="md:w-full lg:max-w-xl xl:max-w-2xl w-full h-full lg:h-72 xl:h-80 object-cover rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-700"
                                src={`${process.env.REACT_APP_BLOB_URL}/www/img/sector/health-aged-care-residential-tv-audio.jpg`}
                                alt="Modern aged care lounge with mounted TV and minimalist media unit—entertainment setup for retirement living."
                            />
                            {/* Dark overlay for hover effect */}
                            <div className="absolute inset-0 bg-black/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                            {/* Animated shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-2xl"></div>
                        </div>
                        <div className="flex flex-col gap-10 md:gap-4 lg:px-0 px-4 text-center lg:text-left">
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 max-w-lg sm:mx-auto md:mx-auto  lg:mx-0 leading-tight">
                                <span className="hero-text-gradient-red">Entertainment </span> that Connects
                            </h2>
                            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
                                Our TV & AV solutions are designed to enhance daily living—streamlined, smart, and simplified. Residents stay connected, entertained, and informed with systems that integrate seamlessly into their environment.
                            </p>
                        </div>
                    </div>
                </div>
            } />

            {/* Subtle section separator */}
            <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-300/50 to-transparent mx-auto"></div>

            {/* Stage It Right */}
            <SlideFromRight Component={
                <div id="secure-casting" className="lg:px-6 xl:px-12 py-14 relative z-10">
                    <div className="flex flex-col lg:flex-row-reverse items-center gap-8 md:gap-10 lg:gap-12 justify-center ">
                        <div className="relative group flex-shrink-0 px-4 md:px-6 lg:px-0 transform transition-all duration-700 hover:-translate-y-3 hover:scale-105">
                            <img
                                loading="eager"
                                className="md:w-full lg:max-w-xl xl:max-w-2xl w-full h-full lg:h-72 xl:h-80 object-cover rounded-2xl shadow-lg group-hover:shadow-2xl transition-all duration-700"
                                src={`${process.env.REACT_APP_BLOB_URL}/www/img/sector/retirement-staging.jpg`}
                                alt="Staged retirement bedroom with adjustable bed and warm, accessible design."
                            />
                            {/* Dark overlay for hover effect */}
                            <div className="absolute inset-0 bg-black/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                            {/* Animated shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-2xl"></div>
                        </div>
                        <div className="flex flex-col gap-10 md:gap-4 lg:px-0 px-4 text-center lg:text-left">
                            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 max-w-lg sm:mx-auto md:mx-auto  lg:mx-0 leading-tight">
                                <span className="hero-text-gradient-red">Stage It Right:</span> Creating Spaces Residents Love

                            </h2>

                            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
                                Our design team works closely with retirement providers to enhance visual appeal and functionality. From layout planning to product selection, we help create lifestyle-rich environments that drive both occupancy and satisfaction.
                            </p>

                        </div>
                    </div>
                </div>
            } />
        </div>
    )
}